import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import {
    filterFinancingPlans,
    getLongestFinancingPlan,
} from "../../apps/checkout/utils";
import { ConfiguratorTypes } from "../../constants";
import { IProduct } from "../../models/catalogue";
import { FinancingPlans } from "../../models/financing";
import { IMonths, WebPageURL, isoMonths } from "../../models/nominals";
import { IPrice } from "../../models/prices";
import {
    PriceType,
    getProductPrice,
    isProductPriceDiscounted,
} from "../../utils/catalogue";
import { format, formatTermLength } from "../../utils/format";
import { Dinero, getDinero, getMonthlyPrice } from "../../utils/money";
import { usePageSetting, useShouldShowPricing } from "../../utils/settings";
import { CardFinancingModal } from "../CardFinancingModal";
import { Link } from "../Link";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    price: IPrice;
    financingPlans?: FinancingPlans;
    variant: IProduct;
    financingLink?: WebPageURL | null;
    configuratorType?: ConfiguratorTypes;
    showFinancingModal?: boolean;
    overrideFinancingCopy?: string;
    extraClasses?: string;
    alternatePriceView?: boolean;
    openNewWindow?: boolean;
}>;

const getLongestActivePlan = (
    financingPlans?: FinancingPlans,
): ReturnType<typeof getLongestFinancingPlan> => {
    if (financingPlans) {
        // Before retrieving the longest active financing plan, narrow down the selection to include only plans with a 0% APR
        const filteredFinancingPlans = filterFinancingPlans(financingPlans);
        return getLongestFinancingPlan(filteredFinancingPlans);
    }
    return null;
};

export const ConfiguratorFinancing = (props: IProps): React.ReactNode => {
    const shouldShowPricing = useShouldShowPricing();
    const appSlug = usePageSetting("app_slug");
    // Do not show financing in Configurator for products that are marked as in-store only
    if (
        (props.variant.attributes.in_store_only &&
            props.variant.attributes.in_store_only.value) ||
        !shouldShowPricing
    ) {
        return null;
    }

    // 1-800-Pillows ships without financing, so the slot stays hidden until
    // the real Affirm integration lands. Gated by appSlug so other sites are
    // unaffected. Once Affirm backend ships, swap the check to:
    //   const hasAffirmPlan = props.financingPlans?.some(
    //       (plan) => plan.backend_type === BackendType.AFFIRM,
    //   );
    if (appSlug === AppSlug.ONE800PILLOWS) {
        return null;
    }

    const totalPrice = getProductPrice(props.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });

    if (!props.price.per_month_term_length) {
        if (!props.showFinancingModal) {
            return null;
        }
        return (
            <CardFinancingModal
                price={totalPrice}
                title={props.variant.title}
                linkText={t`Special financing available with qualifying order`}
            />
        );
    }

    if (props.overrideFinancingCopy) {
        return (
            <span className="configurator__financing">
                <Link
                    className="configurator__financing-link"
                    href={props.financingLink ? props.financingLink : null}
                    target={!props.openNewWindow ? "_self" : "_blank"}
                >
                    <span className="configurator__financing--underline">
                        {props.overrideFinancingCopy}
                    </span>
                </Link>
            </span>
        );
    }

    // As products are often not present in the basket when viewing their PDPs, we cannot directly utilize the financing details associated with
    // the variant's price object, as is done during the checkout process, for product-specific financing advertisement purposes. Therefore,
    // our initial step is to attempt to obtain the longest active financing option based on only the variant, rather than relying on the current basket status.
    let perMonthTermLength: IMonths | null | undefined;
    let pricePerMonth: Dinero | null | undefined;
    const longestActivePlan = getLongestActivePlan(props.financingPlans);
    if (
        longestActivePlan &&
        isoMonths.unwrap(longestActivePlan.term_months) > 0
    ) {
        perMonthTermLength = longestActivePlan.term_months;
        pricePerMonth = getMonthlyPrice(totalPrice, perMonthTermLength);
    } else {
        const hasVariantPriceFinancing =
            props.variant.price.enable_per_month_pricing &&
            props.variant.price.per_month &&
            props.variant.price.per_month_term_length;
        perMonthTermLength = hasVariantPriceFinancing
            ? props.variant.price.per_month_term_length
            : props.price.per_month_term_length;
        pricePerMonth = hasVariantPriceFinancing
            ? getDinero(props.variant.price.per_month)
            : getMonthlyPrice(totalPrice, props.price.per_month_term_length);
    }

    // Build class names for financing
    const financingClasses = classNames({
        "configurator__financing": true,
        [`configurator__financing--${props.extraClasses}`]:
            !!props.extraClasses,
        [`configurator__financing--${props.configuratorType}`]: true,
        "configurator__financing--alternate": props.alternatePriceView,
    });
    const financingOrClasses = classNames({
        "configurator__financing-or": true,
        "configurator__financing-or--alternate": props.alternatePriceView,
    });
    const financingLinkClasses = classNames({
        "configurator__financing-link": true,
        "configurator__financing-link--discounted": isProductPriceDiscounted(
            props.price,
        ),
        "configurator__financing-link--alternate": props.alternatePriceView,
    });
    const financingPaymentsClasses = classNames({
        "configurator__financing-payments": true,
        "configurator__financing-payments--alternate": props.alternatePriceView,
    });
    const financingContainerClasses = classNames({
        "configurator__price--financing": true,
        [`configurator__price--financing-${props.configuratorType}`]: true,
        "configurator__price--financing-alternate": props.alternatePriceView,
        "no-financing-text":
            (!props.price.per_month || !props.price.per_month_term_length) &&
            !props.showFinancingModal,
    });

    return (
        <>
            <div className={financingContainerClasses}>
                <span className={financingOrClasses}>or </span>
                <span className={financingClasses}>
                    <Link
                        className={financingLinkClasses}
                        href={props.financingLink ? props.financingLink : null}
                        target={!props.openNewWindow ? "_top" : "_blank"}
                    >
                        <span className="configurator__financing--underline">
                            {format.money(pricePerMonth)}/mo.
                        </span>
                        <sup>2</sup> {props.alternatePriceView && <br />}
                        for{" "}
                        <span className="configurator__financing--underline">
                            {perMonthTermLength
                                ? formatTermLength(perMonthTermLength)
                                : ""}{" "}
                            months
                        </span>
                        <sup>1</sup> <br />
                        <span className={financingPaymentsClasses}>
                            (total payments of {format.money(totalPrice)})
                        </span>
                    </Link>
                </span>
            </div>
        </>
    );
};
